import { z } from 'zod';

export const LEAD_STAGES = ['new', 'contacted', 'qualified', 'booked', 'won', 'lost'] as const;
export const LeadStageSchema = z.enum(LEAD_STAGES);
export type LeadStage = z.infer<typeof LeadStageSchema>;

export const CONVERSATION_CHANNELS = ['sms'] as const;
export const ConversationChannelSchema = z.enum(CONVERSATION_CHANNELS);
export type ConversationChannel = z.infer<typeof ConversationChannelSchema>;

export const CONVERSATION_STATUSES = ['open', 'closed'] as const;
export const ConversationStatusSchema = z.enum(CONVERSATION_STATUSES);
export type ConversationStatus = z.infer<typeof ConversationStatusSchema>;

export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const;
export const MessageDirectionSchema = z.enum(MESSAGE_DIRECTIONS);
export type MessageDirection = z.infer<typeof MessageDirectionSchema>;

export const MESSAGE_STATUSES = ['queued', 'sent', 'delivered', 'failed', 'received'] as const;
export const MessageStatusSchema = z.enum(MESSAGE_STATUSES);
export type MessageStatus = z.infer<typeof MessageStatusSchema>;

export const LEAD_SOURCES = ['missed_call', 'inbound_sms', 'manual', 'form', 'voice'] as const;
export const LeadSourceSchema = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof LeadSourceSchema>;

export const CALL_DIRECTIONS = ['inbound', 'outbound'] as const;
export const CallDirectionSchema = z.enum(CALL_DIRECTIONS);
export type CallDirection = z.infer<typeof CallDirectionSchema>;

export const CALL_PROVIDERS = ['twilio', 'vapi'] as const;
export const CallProviderSchema = z.enum(CALL_PROVIDERS);
export type CallProvider = z.infer<typeof CallProviderSchema>;

// ── Contact ────────────────────────────────────────────────────────────────

export const ContactSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  tags: z.array(z.string()),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const ContactListEntrySchema = ContactSchema;
export type ContactListEntry = z.infer<typeof ContactListEntrySchema>;

// ── Conversation ───────────────────────────────────────────────────────────

export const ConversationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  contactId: z.string(),
  phoneNumberId: z.string(),
  channel: z.string(),
  status: ConversationStatusSchema,
  assignedUserId: z.string().nullable(),
  lastMessageAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationAssignBodySchema = z.object({
  assignedUserId: z.string().min(1),
});
export type ConversationAssignBody = z.infer<typeof ConversationAssignBodySchema>;

export const ConversationCloseBodySchema = z.object({}).optional();
export type ConversationCloseBody = z.infer<typeof ConversationCloseBodySchema>;

export const ConversationUpdateStatusBodySchema = z.object({
  status: ConversationStatusSchema,
});
export type ConversationUpdateStatusBody = z.infer<typeof ConversationUpdateStatusBodySchema>;

export const ConversationListQuerySchema = z.object({
  status: ConversationStatusSchema.optional(),
  assigneeId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().optional(),
});
export type ConversationListQuery = z.infer<typeof ConversationListQuerySchema>;

// ── Message ────────────────────────────────────────────────────────────────

export const MessageSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  conversationId: z.string(),
  direction: MessageDirectionSchema,
  body: z.string(),
  status: MessageStatusSchema,
  provider: z.string(),
  providerSid: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorDetail: z.string().nullable(),
  sentByUserId: z.string().nullable(),
  segments: z.number(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Message = z.infer<typeof MessageSchema>;

export const MessageSendBodySchema = z.object({
  body: z.string().min(1).max(1600),
});
export type MessageSendBody = z.infer<typeof MessageSendBodySchema>;

export const MessageListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().optional(),
});
export type MessageListQuery = z.infer<typeof MessageListQuerySchema>;

// ── Call ───────────────────────────────────────────────────────────────────

export const CallSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  phoneNumberId: z.string(),
  contactId: z.string().nullable(),
  direction: CallDirectionSchema,
  provider: CallProviderSchema,
  providerSid: z.string(),
  carrierCallSid: z.string().nullable(),
  status: z.string(),
  isMissed: z.boolean(),
  fromE164: z.string(),
  durationSeconds: z.number().nullable(),
  transcript: z.string().nullable(),
  summary: z.string().nullable(),
  qualification: z.record(z.string(), z.unknown()),
  recordingObjectKey: z.string().nullable(),
  startedAt: z.number(),
  endedAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Call = z.infer<typeof CallSchema>;

export const CallListQuerySchema = z.object({
  missed: z.coerce.boolean().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().optional(),
});
export type CallListQuery = z.infer<typeof CallListQuerySchema>;

// ── Lead ───────────────────────────────────────────────────────────────────

export const LeadSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  contactId: z.string(),
  source: LeadSourceSchema,
  sourceCallId: z.string().nullable(),
  stage: LeadStageSchema,
  ownerUserId: z.string().nullable(),
  estimatedValueCents: z.number().nullable(),
  attributedRevenueCents: z.number().nullable(),
  qualification: z.record(z.string(), z.unknown()),
  lostReason: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Lead = z.infer<typeof LeadSchema>;

export const LeadUpdateStageBodySchema = z.object({
  stage: LeadStageSchema,
  lostReason: z.string().optional(),
  attributedRevenueCents: z.number().int().positive().optional(),
});
export type LeadUpdateStageBody = z.infer<typeof LeadUpdateStageBodySchema>;

export const LeadUpdateQualificationBodySchema = z.object({
  qualification: z.record(z.string(), z.unknown()),
});
export type LeadUpdateQualificationBody = z.infer<typeof LeadUpdateQualificationBodySchema>;

export const LeadAssignBodySchema = z.object({
  ownerUserId: z.string().min(1),
});
export type LeadAssignBody = z.infer<typeof LeadAssignBodySchema>;

export const LeadListQuerySchema = z.object({
  stage: LeadStageSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().optional(),
});
export type LeadListQuery = z.infer<typeof LeadListQuerySchema>;

// ── Paginated list envelope ────────────────────────────────────────────────

export interface ListResponse<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * Zod form of `ListResponse<T>` — the ONE definition of the paginated list
 * envelope, so the SPA validates exactly what the Worker returns. Hand-written
 * per-domain copies drifted here once (client schemas named the array
 * `conversations`/`leads`/`calls`/…), which parsed nothing real and broke every
 * list view in production.
 */
export function listResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

// Response schemas consumed by both the Worker contract tests and the SPA.
// Detail and mutation routes return the resource directly inside the standard
// HTTP envelope; they are deliberately not wrapped in domain-specific keys.
export const ConversationListResponseSchema = listResponseSchema(ConversationSchema);
export const ConversationResponseSchema = ConversationSchema;
export const MessageListResponseSchema = listResponseSchema(MessageSchema);
export const MessageResponseSchema = MessageSchema;
export const CallListResponseSchema = listResponseSchema(CallSchema);
export const CallResponseSchema = CallSchema;
export const LeadListResponseSchema = listResponseSchema(LeadSchema);
export const LeadResponseSchema = LeadSchema;
export const ContactListResponseSchema = listResponseSchema(ContactSchema);
export const ContactResponseSchema = ContactSchema;

// ── Lead stage transition validation ──────────────────────────────────────

export const STAGE_TRANSITIONS: Record<LeadStage, LeadStage[]> = {
  // A lead can be booked directly from any active pre-booked stage — a public
  // form or a rep can book an appointment before the lead is formally qualified.
  new: ['contacted', 'qualified', 'booked', 'lost'],
  contacted: ['qualified', 'booked', 'lost'],
  qualified: ['booked', 'lost'],
  booked: ['won', 'lost'],
  won: [],
  lost: [],
};

export function isValidStageTransition(from: LeadStage, to: LeadStage): boolean {
  if (from === to) return true;
  return STAGE_TRANSITIONS[from]?.includes(to) ?? false;
}
